import React, { useState } from "react";

import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

import { faCopy, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { apiKeysApi } from "../../../services/apiKeyServices";
import InformationModal from "../../InformationModal";
import Loader from "../../Loader";

/** POST /api/api-keys — plaintext field name may vary; return null if none found. */
const extractCreatedApiKeyPlaintext = (result) => {
  if (result == null) {
    return null;
  }
  if (typeof result === "string" && result.trim()) {
    return result.trim();
  }
  if (typeof result !== "object") {
    return null;
  }

  const nested =
    result.data != null && typeof result.data === "object"
      ? result.data
      : result.payload != null && typeof result.payload === "object"
        ? result.payload
        : null;

  const candidates = [
    result.key,
    result.apiKey,
    result.value,
    result.token,
    result.secret,
    nested?.key,
    nested?.apiKey,
    nested?.value,
    nested?.token,
  ];

  const first = candidates.find(
    (v) => typeof v === "string" && v.trim().length > 0,
  );
  return first ? first.trim() : null;
};

const ApiKeyModal = ({ onClose, onSubmit }) => {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  const doSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await apiKeysApi.createApiKey({ name });
      setLoading(false);
      const plaintext = extractCreatedApiKeyPlaintext(result);
      if (!plaintext) {
        setInformationModal({
          isOpen: true,
          title: "Create API Key",
          body:
            "The key was created but the response did not include a readable key value. Refresh the list and revoke this key if you are unsure it was created correctly, then contact support.",
        });
        return;
      }
      setCreatedKey(plaintext);
    } catch (err) {
      setLoading(false);
      setInformationModal({
        isOpen: true,
        title: "Create API Key",
        body:
          err?.response?.data?.[0]?.msg ||
          "There was an error with your request.",
      });
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(createdKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDone = () => {
    onSubmit();
  };

  const closeBtn = (
    <Button className="close" color="none" onClick={onClose}>
      &times;
    </Button>
  );

  if (informationModal.isOpen) {
    return (
      <InformationModal
        title={informationModal.title}
        body={informationModal.body}
        onClose={() =>
          setInformationModal({ isOpen: false, title: "", body: "" })
        }
      />
    );
  }

  return (
    <Modal isOpen={true} size="md">
      <ModalHeader
        className="d-flex justify-content-between"
        close={createdKey ? null : closeBtn}
      >
        {createdKey ? "API Key Created" : "Create API Key"}
      </ModalHeader>

      {createdKey ? (
        <>
          <ModalBody>
            <div className="alert alert-warning px-1 mb-3">
              <strong className="pl-1">Save your API key now.</strong> You won't
              be able to see it again after closing this dialog.
            </div>
            <FormGroup>
              <Label>Your API Key</Label>
              <InputGroup>
                <Input
                  type={showKey ? "text" : "password"}
                  value={createdKey}
                  readOnly
                  className="font-monospace"
                />
                <InputGroupText
                  className="cursor-pointer border-left-0"
                  onClick={() => setShowKey((v) => !v)}
                  style={{ cursor: "pointer" }}
                >
                  <FontAwesomeIcon icon={showKey ? faEyeSlash : faEye} />
                </InputGroupText>
                <Button
                  color={copied ? "success" : "secondary"}
                  onClick={handleCopy}
                  title="Copy to clipboard"
                >
                  <FontAwesomeIcon icon={faCopy} className="mr-1" />
                  {copied ? "Copied!" : "Copy"}
                </Button>
              </InputGroup>
              <small className="text-muted mt-1 d-block">
                This key grants API access. Keep it secret and secure.
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter className="justify-content-end">
            <Button color="primary" onClick={handleDone}>
              Done
            </Button>
          </ModalFooter>
        </>
      ) : (
        <Form onSubmit={doSubmit}>
          <ModalBody>
            <Row>
              {loading ? (
                <Col className="col-12 text-center py-3">
                  <Loader size="sm" />
                </Col>
              ) : (
                <Col className="col-12">
                  <FormGroup className="mb-0">
                    <Label for="apiKeyName">
                      <span>Name</span>
                      <span className="text-danger ml-2">*</span>
                    </Label>
                    <Input
                      maxLength="100"
                      type="text"
                      name="apiKeyName"
                      id="apiKeyName"
                      placeholder="e.g. Production Integration, CI/CD Pipeline..."
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                    <small className="text-muted">
                      A descriptive name helps identify where this key is used.
                    </small>
                  </FormGroup>
                </Col>
              )}
            </Row>
          </ModalBody>
          <ModalFooter>
            <Col>
              <Row className="justify-content-between">
                <Button color="secondary" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={loading}>
                  Generate Key
                </Button>
              </Row>
            </Col>
          </ModalFooter>
        </Form>
      )}
    </Modal>
  );
};

export default ApiKeyModal;
